import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineX } from "react-icons/hi";
import classNames from "classnames";

const PaSearchSelect = ({
  label,
  value = null,
  onChange,
  onSearch,
  placeholder = "Search by name or email",
  minLength = 2,
  disabled = false,
  emptyMessage = "No matches",
  hint = null,
}) => {
  const listIdRef = useRef(
    `pa-search-select-${Math.random().toString(36).slice(2, 9)}`
  );
  const containerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const trimmed = query.trim();
    if (trimmed.length < minLength) {
      setOptions([]);
      setSearchError(trimmed.length > 0 ? `Type at least ${minLength} characters` : "");
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setSearchError("");

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await onSearch(trimmed);
        setOptions(Array.isArray(results) ? results : []);
      } catch {
        setOptions([]);
        setSearchError("Search failed");
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [minLength, onSearch, open, query]);

  const handleClear = useCallback(() => {
    setQuery("");
    setOptions([]);
    setOpen(false);
    onChange(null);
  }, [onChange]);

  const handleSelect = useCallback(
    (option) => {
      setQuery("");
      setOptions([]);
      setOpen(false);
      onChange(option);
    },
    [onChange]
  );

  const inputClassName =
    "block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-visible:border-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-500";

  return (
    <div ref={containerRef} className="relative block">
      {label ? (
        <span className="mb-1 block text-xs font-medium text-gray-500">{label}</span>
      ) : null}

      {value ? (
        <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{value.label}</p>
            {value.description ? (
              <p className="truncate text-xs text-gray-500">{value.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="shrink-0 rounded p-1 text-gray-500 hover:bg-white hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
            aria-label={`Clear ${label || "selection"}`}
          >
            <HiOutlineX className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            type="search"
            value={query}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className={inputClassName}
            aria-controls={listIdRef.current}
            aria-expanded={open}
            autoComplete="off"
          />
          {hint ? <p className="mt-1 text-xs text-gray-400">{hint}</p> : null}
          {open ? (
            <div
              id={listIdRef.current}
              className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            >
              {loading ? (
                <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>
              ) : searchError ? (
                <p className="px-3 py-2 text-sm text-gray-500">{searchError}</p>
              ) : options.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</p>
              ) : (
                options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={classNames(
                      "block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none",
                      value?.id === option.id && "bg-indigo-50"
                    )}
                  >
                    <span className="font-medium text-gray-900">{option.label}</span>
                    {option.description ? (
                      <span className="mt-0.5 block truncate text-xs text-gray-500">
                        {option.description}
                      </span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default PaSearchSelect;
